import React, { useEffect, useRef } from 'react';

/** `<chat-component>` is registered by `public/index.html` (unpkg: React UMD + Feazy NEO web). */

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements {
            'chat-component': any;
        }
    }
}

export interface IFeazyChatProps {
    promptId: string;
    chatTitle?: string;
    theme?: 'neutral-theme' | 'light-theme' | 'dark-theme';
    isDialogVisible?: boolean;
    logoPath?: string;
    trackingId?: string;
    baseUrl?: string;
}

interface IChatComponentElement extends HTMLElement {
    promptId?: string;
    isDialogVisible?: boolean;
    baseUrl?: string;
}

export const FeazyChat: React.FC<IFeazyChatProps> = ({ promptId, isDialogVisible = false, baseUrl = 'https://api.feazy.ai' }) => {
    const chatRef = useRef<IChatComponentElement | null>(null);

    useEffect(() => {
        const chatElement = chatRef.current;
        if (!chatElement) return;

        // Set both DOM properties and attributes to support different web-component implementations.
        chatElement.promptId = promptId;
        chatElement.isDialogVisible = isDialogVisible;
        chatElement.baseUrl = baseUrl;

        chatElement.setAttribute('prompt-id', promptId);
        chatElement.setAttribute('base-url', baseUrl);
        chatElement.setAttribute('is-dialog-visible', String(isDialogVisible));
        chatElement.setAttribute('remote-config', '');
    }, [baseUrl, isDialogVisible, promptId]);

    const fluentUIStyle = {
        '--primary-color': '#0078d4',
        '--secondary-color': '#f3f2f1',
        '--text-color': '#323130',
        '--border-color': '#d0d0d0',
        '--bot-message-bg': '#eff6fc',
        '--user-message-bg': '#0078d4',
        '--user-message-text': '#ffffff',
        '--input-bg': '#ffffff',
        '--header-bg': '#0078d4',
        '--header-text': '#ffffff',
        '--chat-button-bg': '#0078d4',
        '--chat-button-color': '#ffffff',
        '--chat-window-bg': '#ffffff',
        '--send-button-bg': '#0078d4',
        '--send-button-color': '#ffffff',
        '--copy-button-bg': '#f3f2f1',
        '--copy-button-color': '#323130',
        '--divider-color': '#d0d0d0',
        '--quick-reply-bg': '#f3f2f1',
        '--quick-reply-color': '#323130',
        '--quick-reply-border': '#d0d0d0',
        '--quick-reply-hover-bg': '#deecf9',
        '--quick-reply-hover-color': '#0078d4',
        '--dropdown-font-color': '#323130'
    } as React.CSSProperties;

    return (
        <div style={fluentUIStyle}>
            <chat-component ref={chatRef} />
        </div>
    );
};
